import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  REPORT_ENTITIES,
  REPORT_ENTITY_LABELS,
  REPORT_DEFAULT_RUN_TIMES,
  REPORT_FREQUENCIES,
  REPORT_FREQUENCY_LABELS,
  REPORT_MAX_RUN_TIMES,
  REPORT_MODES,
  REPORT_MODE_LABELS,
  REPORT_SQL_MAX_LENGTH,
  reportColumnsFor,
  reportFiltersFor,
  type CreateReportInput,
  type Report,
  type ReportEntity,
  type ReportFilterDef,
  type ReportFrequency,
  type ReportMode,
  type ReportSectionInput,
  type SortDirection,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { RichTextEditor } from '../../components/ui/RichText.js';
import { useCreateReport, useDeleteReport, useUpdateReport } from './api.js';

interface ReportFormModalProps {
  isOpen: boolean;
  /** Existing report to edit, or null to create a new one. */
  report: Report | null;
  onClose: () => void;
}

interface SectionState {
  /** undefined = new section (no id yet) */
  id?: string;
  sortOrder: number;
  header: string;
  footer: string;
  sqlQuery: string;
}

interface FormState {
  name: string;
  description: string;
  mode: ReportMode;
  entity: ReportEntity;
  sqlQuery: string;
  /** Multi-section SQL sections (sql mode only). */
  sections: SectionState[];
  columns: string[];
  filters: Record<string, string>;
  sortBy: string;
  sortDir: SortDirection;
  frequency: ReportFrequency;
  runTimes: string[];
  skipWeekends: boolean;
  sendIfEmpty: boolean;
  recipients: string;
  attachCsv: boolean;
  attachPdf: boolean;
  enabled: boolean;
}

function emptyForm(): FormState {
  return {
    name: '',
    description: '',
    mode: 'builder',
    entity: 'time_entries',
    sqlQuery: '',
    sections: [],
    columns: [],
    filters: {},
    sortBy: '',
    sortDir: 'asc',
    frequency: 'manual',
    runTimes: [...REPORT_DEFAULT_RUN_TIMES],
    skipWeekends: false,
    sendIfEmpty: true,
    recipients: '',
    attachCsv: false,
    attachPdf: false,
    enabled: false,
  };
}

function toFormState(report: Report | null): FormState {
  if (!report) return emptyForm();
  const filters: Record<string, string> = {};
  for (const f of report.filters) filters[f.key] = f.value;
  return {
    name: report.name,
    description: report.description ?? '',
    mode: report.mode,
    entity: report.entity ?? 'time_entries',
    sqlQuery: report.sqlQuery ?? '',
    sections: report.sections.map((s) => ({
      id: s.id,
      sortOrder: s.sortOrder,
      header: s.header ?? '',
      footer: s.footer ?? '',
      sqlQuery: s.sqlQuery,
    })),
    columns: report.columns,
    filters,
    sortBy: report.sortBy ?? '',
    sortDir: report.sortDir,
    frequency: report.frequency,
    runTimes: report.runTimes.length > 0 ? report.runTimes : [...REPORT_DEFAULT_RUN_TIMES],
    skipWeekends: report.skipWeekends,
    sendIfEmpty: report.sendIfEmpty,
    recipients: report.recipients.join(', '),
    attachCsv: report.attachCsv,
    attachPdf: report.attachPdf,
    enabled: report.enabled,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) result[detail.path] = detail.message;
    return result;
  }
  return {};
}

/** Parses a free-form recipients string (comma / space / newline separated) into emails. */
function parseRecipients(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ReportFormModal({
  isOpen,
  report: reportProp,
  onClose,
}: ReportFormModalProps): JSX.Element {
  const [report, setReport] = useState<Report | null>(reportProp);
  const isEdit = report !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(report));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateReport();
  const updateMutation = useUpdateReport();
  const deleteMutation = useDeleteReport();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  // Shared save-mode branches used by every create/update call below.
  const onUpdateSuccess = (): void => {
    if (saveMode.closeAfterSaveRef.current) onClose();
  };
  const onCreateSuccess = (created: Report): void => {
    if (saveMode.closeAfterSaveRef.current) {
      onClose();
    } else {
      setReport(created);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setReport(reportProp);
    }
  }, [isOpen, reportProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(report));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, report, resetSaveMode, resetCreate, resetUpdate]);

  const columnCatalog = useMemo(() => reportColumnsFor(form.entity), [form.entity]);
  const filterCatalog = useMemo(() => reportFiltersFor(form.entity), [form.entity]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save report.'
        : null;

  const setEntity = (entity: ReportEntity): void => {
    // Switching entities invalidates the previously chosen columns/filters/sort.
    setForm((f) => ({ ...f, entity, columns: [], filters: {}, sortBy: '' }));
  };

  const toggleColumn = (key: string): void => {
    setForm((f) => {
      const has = f.columns.includes(key);
      const next = has ? f.columns.filter((c) => c !== key) : [...f.columns, key];
      // Keep selection ordered by the catalog for predictable output.
      const ordered = columnCatalog.filter((c) => next.includes(c.key)).map((c) => c.key);
      return { ...f, columns: ordered };
    });
  };

  const setFilter = (key: string, value: string): void => {
    setForm((f) => ({ ...f, filters: { ...f.filters, [key]: value } }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    const common = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      frequency: form.frequency,
      runTimes:
        form.frequency === 'interval' ? form.runTimes.map((t) => t.trim()).filter(Boolean) : [],
      skipWeekends: form.skipWeekends,
      sendIfEmpty: form.sendIfEmpty,
      recipients: parseRecipients(form.recipients),
      attachCsv: form.attachCsv,
      attachPdf: form.attachPdf,
      enabled: form.enabled,
    };

    if (form.mode === 'sql') {
      const sections: ReportSectionInput[] = form.sections.map((s, idx) => ({
        id: s.id,
        sortOrder: idx,
        header: s.header.trim() || null,
        footer: s.footer.trim() || null,
        sqlQuery: s.sqlQuery.trim(),
      }));

      // Use sections when there are any; otherwise fall back to single sqlQuery.
      const sqlPayload =
        sections.length > 0
          ? { sections, sqlQuery: undefined }
          : { sqlQuery: form.sqlQuery.trim(), sections: [] };

      if (isEdit) {
        updateMutation.mutate(
          { id: report.id, input: { ...common, ...sqlPayload } },
          { onSuccess: onUpdateSuccess },
        );
      } else {
        const payload: CreateReportInput = {
          ...common,
          mode: 'sql',
          ...sqlPayload,
        };
        createMutation.mutate(payload, { onSuccess: onCreateSuccess });
      }
      return;
    }

    const filters = filterCatalog
      .map((def) => ({ key: def.key, value: (form.filters[def.key] ?? '').trim() }))
      .filter((f) => f.value);

    const builder = {
      ...common,
      columns: form.columns,
      filters,
      sortBy: form.sortBy || null,
      sortDir: form.sortDir,
    };

    if (isEdit) {
      updateMutation.mutate({ id: report.id, input: builder }, { onSuccess: onUpdateSuccess });
    } else {
      createMutation.mutate(
        { ...builder, mode: 'builder', entity: form.entity },
        { onSuccess: onCreateSuccess },
      );
    }
  };

  const handleDelete = (): void => {
    if (!report) return;
    deleteMutation.mutate(report.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  const renderFilter = (def: ReportFilterDef): JSX.Element => {
    const value = form.filters[def.key] ?? '';
    if (def.type === 'date') {
      return (
        <TextField
          key={def.key}
          label={def.label}
          type="date"
          value={value}
          onChange={(e) => setFilter(def.key, e.target.value)}
        />
      );
    }
    if (def.type === 'enum') {
      return (
        <SelectField
          key={def.key}
          label={def.label}
          value={value}
          options={[
            { value: '', label: 'Any' },
            ...(def.options ?? []).map((o) => ({ value: o, label: o })),
          ]}
          onChange={(e) => setFilter(def.key, e.target.value)}
        />
      );
    }
    // text + uuid: a plain input (uuid filters accept a pasted record id).
    return (
      <TextField
        key={def.key}
        label={def.label}
        value={value}
        placeholder={def.type === 'uuid' ? 'Paste a record ID…' : undefined}
        onChange={(e) => setFilter(def.key, e.target.value)}
      />
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="wide"
        title={isEdit ? 'Edit report' : 'New report'}
        footer={
          <FormActions
            formId="report-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="report-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label="Name"
              required
              value={form.name}
              maxLength={200}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors.name}
            />
            <SelectField
              label="Report type"
              value={form.mode}
              disabled={isEdit}
              options={REPORT_MODES.map((m) => ({ value: m, label: REPORT_MODE_LABELS[m] }))}
              onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value as ReportMode }))}
            />
          </div>

          <TextAreaField
            label="Description"
            rows={2}
            value={form.description}
            maxLength={2000}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />

          {form.mode === 'sql' ? (
            <div className="flex flex-col gap-4">
              {/* ── Multi-section area ── */}
              {form.sections.map((section, idx) => (
                <fieldset key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <legend className="px-1 text-sm font-semibold text-slate-700">
                      Section {idx + 1}
                    </legend>
                    <div className="flex gap-1">
                      {idx > 0 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setForm((f) => {
                              const next = [...f.sections];
                              const a = next[idx - 1]!;
                              const b = next[idx]!;
                              next[idx - 1] = b;
                              next[idx] = a;
                              return { ...f, sections: next };
                            })
                          }
                        >
                          ↑
                        </Button>
                      ) : null}
                      {idx < form.sections.length - 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setForm((f) => {
                              const next = [...f.sections];
                              const a = next[idx]!;
                              const b = next[idx + 1]!;
                              next[idx] = b;
                              next[idx + 1] = a;
                              return { ...f, sections: next };
                            })
                          }
                        >
                          ↓
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-rose-500 hover:bg-rose-50"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            sections: f.sections.filter((_, i) => i !== idx),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div>
                      <p className="mb-1 text-sm font-medium text-slate-700">
                        Header <span className="font-normal text-slate-400">(optional)</span>
                      </p>
                      <RichTextEditor
                        value={section.header}
                        placeholder="Section header text…"
                        onChange={(html) =>
                          setForm((f) => ({
                            ...f,
                            sections: f.sections.map((s, i) =>
                              i === idx ? { ...s, header: html } : s,
                            ),
                          }))
                        }
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-sm font-medium text-slate-700">SQL query</p>
                      <textarea
                        required
                        rows={8}
                        value={section.sqlQuery}
                        maxLength={REPORT_SQL_MAX_LENGTH}
                        spellCheck={false}
                        placeholder="SELECT TOP (100) ..."
                        style={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        }}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            sections: f.sections.map((s, i) =>
                              i === idx ? { ...s, sqlQuery: e.target.value } : s,
                            ),
                          }))
                        }
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-sm font-medium text-slate-700">
                        Footer <span className="font-normal text-slate-400">(optional)</span>
                      </p>
                      <RichTextEditor
                        value={section.footer}
                        placeholder="Section footer text…"
                        onChange={(html) =>
                          setForm((f) => ({
                            ...f,
                            sections: f.sections.map((s, i) =>
                              i === idx ? { ...s, footer: html } : s,
                            ),
                          }))
                        }
                      />
                    </div>
                  </div>
                </fieldset>
              ))}

              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    sections: [
                      ...f.sections,
                      { sortOrder: f.sections.length, header: '', footer: '', sqlQuery: '' },
                    ],
                  }))
                }
              >
                + Add section
              </Button>

              {/* Legacy single-query fallback when no sections exist */}
              {form.sections.length === 0 ? (
                <fieldset className="rounded-lg border border-slate-200 p-4">
                  <legend className="px-1 text-sm font-medium text-slate-700">Advanced SQL</legend>
                  <TextAreaField
                    label="SQL query"
                    required
                    rows={10}
                    value={form.sqlQuery}
                    maxLength={REPORT_SQL_MAX_LENGTH}
                    spellCheck={false}
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    }}
                    placeholder="SELECT TOP (100) name, total FROM invoice ORDER BY total DESC"
                    onChange={(e) => setForm((f) => ({ ...f, sqlQuery: e.target.value }))}
                    error={fieldErrors.sqlQuery}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Read-only: a single <code>SELECT</code> (or <code>WITH … SELECT</code>)
                    statement. Use <strong>+ Add section</strong> above to create a multi-section
                    report with per-section headers, footers, and queries.
                  </p>
                </fieldset>
              ) : null}
            </div>
          ) : (
            <>
              <SelectField
                label="Data source"
                value={form.entity}
                disabled={isEdit}
                options={REPORT_ENTITIES.map((e) => ({ value: e, label: REPORT_ENTITY_LABELS[e] }))}
                onChange={(e) => setEntity(e.target.value as ReportEntity)}
                error={fieldErrors.entity}
              />

              <fieldset className="rounded-lg border border-slate-200 p-4">
                <legend className="px-1 text-sm font-medium text-slate-700">Columns</legend>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {columnCatalog.map((col) => (
                    <CheckboxField
                      key={col.key}
                      label={col.label}
                      checked={form.columns.includes(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  ))}
                </div>
                {fieldErrors.columns ? (
                  <p className="mt-2 text-xs text-rose-600">{fieldErrors.columns}</p>
                ) : null}
              </fieldset>

              {filterCatalog.length > 0 ? (
                <fieldset className="rounded-lg border border-slate-200 p-4">
                  <legend className="px-1 text-sm font-medium text-slate-700">Filters</legend>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {filterCatalog.map((def) => renderFilter(def))}
                  </div>
                </fieldset>
              ) : null}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <SelectField
                  label="Sort by"
                  value={form.sortBy}
                  options={[
                    { value: '', label: 'Default order' },
                    ...columnCatalog.map((c) => ({ value: c.key, label: c.label })),
                  ]}
                  onChange={(e) => setForm((f) => ({ ...f, sortBy: e.target.value }))}
                />
                <SelectField
                  label="Sort direction"
                  value={form.sortDir}
                  options={[
                    { value: 'asc', label: 'Ascending' },
                    { value: 'desc', label: 'Descending' },
                  ]}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sortDir: e.target.value as SortDirection }))
                  }
                />
              </div>
            </>
          )}

          <fieldset className="rounded-lg border border-slate-200 p-4">
            <legend className="px-1 text-sm font-medium text-slate-700">
              Schedule &amp; delivery
            </legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SelectField
                label="Frequency"
                value={form.frequency}
                options={REPORT_FREQUENCIES.map((fr) => ({
                  value: fr,
                  label: REPORT_FREQUENCY_LABELS[fr],
                }))}
                onChange={(e) =>
                  setForm((f) => ({ ...f, frequency: e.target.value as ReportFrequency }))
                }
              />
              <TextField
                label="Recipients"
                value={form.recipients}
                placeholder="name@example.com, other@example.com"
                onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
                error={fieldErrors.recipients}
              />
            </div>
            {form.frequency === 'interval' ? (
              <div className="mt-3">
                <p className="text-sm font-medium text-slate-700">Run at these times</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Times are in the billing timezone (US Eastern). The scheduler checks every 15
                  minutes, so a run fires at or just after each time.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {form.runTimes.map((time, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1"
                    >
                      <input
                        type="time"
                        value={time}
                        onChange={(e) =>
                          setForm((f) => {
                            const next = [...f.runTimes];
                            next[index] = e.target.value;
                            return { ...f, runTimes: next };
                          })
                        }
                        className="bg-transparent text-sm text-slate-800 focus:outline-none"
                      />
                      <button
                        type="button"
                        aria-label="Remove time"
                        className="text-slate-400 hover:text-rose-600"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            runTimes: f.runTimes.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {form.runTimes.length < REPORT_MAX_RUN_TIMES ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-8 px-2 py-0 text-xs"
                      onClick={() => setForm((f) => ({ ...f, runTimes: [...f.runTimes, '12:00'] }))}
                    >
                      + Add time
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-4">
              <CheckboxField
                label="Attach CSV to email"
                checked={form.attachCsv}
                onChange={(e) => setForm((f) => ({ ...f, attachCsv: e.target.checked }))}
              />
              <CheckboxField
                label="Attach PDF to email"
                checked={form.attachPdf}
                onChange={(e) => setForm((f) => ({ ...f, attachPdf: e.target.checked }))}
              />
              <CheckboxField
                label="Skip weekends"
                checked={form.skipWeekends}
                onChange={(e) => setForm((f) => ({ ...f, skipWeekends: e.target.checked }))}
              />
              <CheckboxField
                label="Send even if no results"
                checked={form.sendIfEmpty}
                onChange={(e) => setForm((f) => ({ ...f, sendIfEmpty: e.target.checked }))}
              />
              <CheckboxField
                label="Enable scheduled delivery"
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              When enabled with a frequency, the report is emailed to the recipients with its
              contents in the message body. &ldquo;Multiple times a day&rdquo; runs at each time you
              set above. Uncheck &ldquo;Send even if no results&rdquo; to skip a run that returns no
              rows.
            </p>
          </fieldset>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete report"
        message={`Delete "${report?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
